import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const S = '#00F5A0';
const G = '#8892A4';

const STORAGE_KEY = 'safeheir_onboarding_dismissed';

const steps = [
  {
    id: 'vault',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="7" width="18" height="14" rx="2"/>
        <path d="M7 7V5a5 5 0 0110 0v2"/>
        <circle cx="12" cy="14" r="1.5"/>
      </svg>
    ),
    title: 'Ajouter un premier secret',
    desc: 'Enregistrez un mot de passe ou une note dans votre coffre-fort chiffré.',
    cta: 'Aller au coffre-fort',
    route: '/vault',
    doneKey: 'vault_items',
  },
  {
    id: 'beneficiary',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="9" cy="7" r="4"/>
        <path d="M2 21v-2a6 6 0 016-6h2"/>
        <path d="M19 15l2 2 4-4"/>
      </svg>
    ),
    title: 'Désigner un bénéficiaire',
    desc: 'Choisissez la personne de confiance qui recevra vos informations.',
    cta: 'Gérer les bénéficiaires',
    route: '/beneficiaries',
    doneKey: 'beneficiaries',
  },
  {
    id: 'checkin',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <polyline points="12 6 12 12 16 14"/>
      </svg>
    ),
    title: 'Configurer votre check-in',
    desc: 'Définissez la fréquence à laquelle SafeHeir doit vous demander confirmation de vie.',
    cta: 'Configurer le check-in',
    route: '/checkin',
    doneKey: 'has_checked_in',
  },
];

const OnboardingChecklist = ({ stats, userId }) => {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const key = `${STORAGE_KEY}_${userId}`;
    if (localStorage.getItem(key) === '1') setDismissed(true);
  }, [userId]);

  const dismiss = () => {
    localStorage.setItem(`${STORAGE_KEY}_${userId}`, '1');
    setDismissed(true);
  };

  const doneMap = {
    vault_items:   (stats?.total_vault_items ?? 0) > 0,
    beneficiaries: (stats?.total_beneficiaries ?? 0) > 0,
    has_checked_in: stats?.has_ever_checked_in ?? false,
  };

  const completedCount = steps.filter(s => doneMap[s.doneKey]).length;
  const allDone = completedCount === steps.length;

  if (dismissed || allDone) return null;

  return (
    <div style={{
      background: 'rgba(0,245,160,0.04)',
      border: '1px solid rgba(0,245,160,0.15)',
      borderRadius: 16,
      padding: '20px 24px',
      marginBottom: 24,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', marginBottom: 2 }}>
            Bienvenue sur SafeHeir
          </div>
          <div style={{ fontSize: 13, color: G }}>
            {completedCount}/{steps.length} étapes complétées
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {steps.map(s => (
              <div key={s.id} style={{
                width: 8, height: 8, borderRadius: '50%',
                background: doneMap[s.doneKey] ? S : 'rgba(255,255,255,0.12)',
              }} />
            ))}
          </div>
          <button
            onClick={dismiss}
            style={{ background: 'none', border: 'none', color: G, cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '2px 4px' }}
            title="Masquer"
          >
            ×
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {steps.map(step => {
          const done = doneMap[step.doneKey];
          return (
            <div
              key={step.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 14,
                background: done ? 'rgba(0,245,160,0.06)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${done ? 'rgba(0,245,160,0.2)' : 'rgba(255,255,255,0.06)'}`,
                borderRadius: 10, padding: '12px 16px',
                opacity: done ? 0.75 : 1,
              }}
            >
              <div style={{
                width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                background: done ? 'rgba(0,245,160,0.12)' : 'rgba(255,255,255,0.05)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: done ? S : G,
              }}>
                {done
                  ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={S} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  : step.icon
                }
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: done ? G : '#fff', textDecoration: done ? 'line-through' : 'none' }}>
                  {step.title}
                </div>
                {!done && (
                  <div style={{ fontSize: 12, color: G, marginTop: 2 }}>{step.desc}</div>
                )}
              </div>
              {!done && (
                <button
                  onClick={() => navigate(step.route)}
                  style={{
                    background: 'rgba(0,245,160,0.1)', border: '1px solid rgba(0,245,160,0.25)',
                    borderRadius: 6, padding: '6px 12px', color: S,
                    fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                  }}
                >
                  {step.cta}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OnboardingChecklist;
